import { GridsterItem } from "angular-gridster2";
import { Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { WidgetDataService } from "../data/widget-data.service";
import { IBaseWidgetConfig, WidgetType } from "./widget-list";
import { DxFormComponent } from "devextreme-angular";
import { BaseComponent } from "src/app/core/abstract/base.component";
import { BehaviorSubject, Observable, Subscription, takeUntil } from "rxjs";
import { RemoteDataWrapperService } from "../../../../../shared/services/remote-data/remote-data-wrapper.service"

/**
 * Layout config.
 *
 * @export
 * @interface Layout
 */
export interface ILayout {
    name: string;
    icon: string;
    layout: LayoutConfiguration[];
}

/**
 * Layout configuration for gridster.
 *
 * @export
 * @interface LayoutConfiguration
 */
export interface LayoutConfiguration {
    cols: number;
    rows: number;
    x: number;
    y: number;
}

/**
 * Interface for API - FE communication.
 *
 * @export
 * @interface IBaseWidget
 */
export interface IBaseWidget {
    id: string;
    type: WidgetType;
    title?: string;
    subtitle?: string;
    gridsterWidgetOptions: GridsterItem;
    options?: IBaseWidgetConfig;
    hasRefreshButton: boolean;
}

/**
 * Abstract class for widget.
 *
 * @export
 * @abstract
 * @class BaseWidgetComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @template T
 */
@Component({
    selector: 'base-widget',
    template: ''
})
export abstract class BaseWidgetComponent<T extends IBaseWidgetConfig> extends BaseComponent implements OnInit {
    
    /**
     * Widget options.
     *
     * @type {T}
     * @memberof BaseWidgetComponent
     */
    public options?: T;

    protected widgetMaxHeight: number = 0;
    private _supplierStateSub?: Subscription;
    
    constructor(
        protected widgetDataService: WidgetDataService<T>,
        protected remoteDataWrapperService: RemoteDataWrapperService,
    ){
        super();

        this.widgetDataService.onResize$.pipe(takeUntil(this.unSubscriber)).subscribe((val) => {
            if (val?.height) {
                this.widgetMaxHeight = val.height;
            }
        })
    }

    ngOnInit(): void {
        this.widgetDataService.options$.pipe(takeUntil(this.unSubscriber)).subscribe(value => {
            this.defaultSetOptions(value);
            this.setOptions(value);
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._supplierStateSub?.unsubscribe();
        if (this.options && this.options.machineName) {
            this.remoteDataWrapperService.watchRemoteDataUpdates(this.options.machineName, false);
        }
    }

    protected defaultSetOptions(options: T | undefined): void {
        if (options) {
            if (options.machineName && options.machineName !== this.options?.machineName) {
                if (this.options?.machineName) {
                    this.remoteDataWrapperService.watchRemoteDataUpdates(this.options.machineName, false);
                }

                this.remoteDataWrapperService.watchRemoteDataUpdates(options.machineName, true);

                this._supplierStateSub?.unsubscribe();
                this._supplierStateSub = this.remoteDataWrapperService.getSupplierState(options.machineName).pipe(takeUntil(this.unSubscriber)).subscribe(val => {
                    this.supplierState.next(val);
                    this.supplierStateChnged(val);
                });
            }
        }
    }

    protected supplierStateChnged(val: boolean): void {
        // do nothing
        // only for override
    }
    
    /**
     * Options setter, to allow implement custom logic when options are given.
     *
     * @protected
     * @abstract
     * @param {(T | undefined)} options
     * @memberof BaseWidgetComponent
     */
    protected abstract setOptions(options: T | undefined): void;

    protected supplierState: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

    /**
     * Represents state of supplier
     * If null, widget does not have SIGNALR supplier (f.e Machine Info)
     * @protected
     * @abstract
     * @return {*}  {(Observable<boolean | null>)}
     * @memberof BaseWidgetComponent
     */
    public supplierState$(): Observable<boolean | null> {
        return this.supplierState.asObservable();
    }

    public onRefresh(): void {
        // initially do nothing
    }
}

/**
 * Abstract class for widget settings.
 *
 * @export
 * @abstract
 * @class BaseWidgetSettingsComponent
 * @template T
 */
@Component({
    //selector: 'base-widget-settings',
    template: ''

})
export abstract class BaseWidgetSettingsComponent<T extends IBaseWidgetConfig> {
    
    /**
     * Form data.
     *
     * @type {T}
     * @memberof BaseWidgetSettingsComponent
     */
    public formData?: T;

    
    /**
     * Popup visibility.
     *
     * @type {boolean}
     * @memberof BaseWidgetSettingsComponent
     */
    public visible: boolean = true;

    
    /**
     * Toolbar items for popup header.
     *
     * @protected
     * @memberof BaseWidgetSettingsComponent
     */
    protected toolbarItems = [
        {
            widget: 'dxButton',
            options: {
            text: 'OK',
            type: 'success',
            onClick: () => this.onSubmitModal()
            },
            location: 'after'
        },
        {
            widget: 'dxButton',
            options: {
            text: 'CLOSE',
            onClick: () => this.onCloseModal()
            },
            location: 'after'
        }
    ];

    
    /**
     * Event emmiter when popup is closed.
     *
     * @type {(EventEmitter<T | null>)}
     * @memberof BaseWidgetSettingsComponent
     */
    @Output() onClose: EventEmitter<T | null> = new EventEmitter();

    
    /**
     * Form instance component for validation, etc.
     *
     * @type {DxFormComponent}
     * @memberof BaseWidgetSettingsComponent
     */
    @ViewChild(DxFormComponent) form?: DxFormComponent;

    constructor(protected widgetDataService: WidgetDataService<T>) {

    }
    
    /**
     * Form data getter.
     *
     * @return {*}  {(T | undefined)}
     * @memberof BaseWidgetSettingsComponent
     */
    public getFormData(): T | undefined {
        return this.formData;
    }

    
    /**
     * Form data setter.
     *
     * @param {IWidgetConfig} data
     * @memberof BaseWidgetSettingsComponent
     */
    public setFormData(data: T): void {
        this.formData = {...data} as T;
    }

    
    /**
     * On modal close without saving changes.
     *
     * @protected
     * @memberof BaseWidgetSettingsComponent
     */
    protected onCloseModal(): void {
      this.visible = false;
      this.onClose.emit(null);
    }

    
    /**
     * On modal close with saving changes if are correct.
     *
     * @protected
     * @memberof BaseWidgetSettingsComponent
     */
    protected onSubmitModal(): void {
      const result = this.form?.instance.validate();

      if (result?.isValid) {
        this.visible = false;  
        this.onClose.emit(this.formData);
        this.widgetDataService.setOptions(this.formData);
      }
    }
}